import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import { CssSelectTextField } from "./CssTextField";
import { useDispatch, useSelector } from "react-redux";
import { getMasterData } from "../../redux/actions/appActions";
import {
  SET_CITIES,
  SET_DEPARTMENTS,
  SET_HIRING_COMPANIES,
  SET_INDUSTRIES,
  SET_JOB_ROLES,
} from "../../redux/actions/actionTypes";
import { getKeyValuePairs } from "../../redux/services/utils";
import {
  Badge,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  MenuItem,
  Radio,
  Select,
  Typography,
} from "@mui/material";
import Close from "@mui/icons-material/Close";
import MultiSelect from "./MultiSelect";
import { JOB_TYPES, WORK_MODES } from "../../constants/AppVarConstants";

type FormValues = Yup.InferType<any>;

const JobType = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontSize: "16px",
}));

const defaultValues = {
  type: "",
  company: [],
  function: null,
  jobRole: [],
  industry: [],
  jobType: [],
  workMode: [],
  location: [],
  minSal: null,
  maxSal: null,
  minExp: "",
  maxExp: "",
};

const jobTypes = [
  { label: "Internship", value: "Internship" },
  { label: "Job", value: "Job" },
];

const schema = Yup.object().shape({
  type: Yup.string(),
  company: Yup.array(),
  function: Yup.object()
    .shape({
      label: Yup.string(),
      value: Yup.string(),
    })
    .notRequired(),
  jobRole: Yup.array(),
  industry: Yup.array(),
  jobType: Yup.array(),
  workMode: Yup.array(),
  location: Yup.array(),
  minSal: Yup.number()
    .nullable()
    .transform((value) => (value === "" ? null : value))
    .notRequired()
    .test("is-valid-minSal", "Minimum salary must be less than maximum salary", function (value) {
      const { maxSal } = this.parent;
      if (value != null && maxSal != null) {
        return value <= maxSal;
      }
      return true;
    }),
  maxSal: Yup.number()
    .nullable()
    .transform((value) => (value === "" ? null : value))
    .notRequired()
    .test("is-valid-maxSal", "Maximum salary must be greater than minimum salary", function (value) {
      const { minSal } = this.parent;
      if (value != null && minSal != null) {
        return value >= minSal;
      }
      return true;
    })
    .when("minSal", {
      is: (minSal: any) => minSal != null,
      then: (schema: any) => schema.required("Maximum salary is required when minimum salary is set."),
    }),
  minExp: Yup.number()
    .transform((value, originalValue) => (originalValue.trim() === "" ? null : value))
    .nullable()
    .notRequired(),
  maxExp: Yup.number()
    .transform((value, originalValue) => (originalValue.trim() === "" ? null : value))
    .nullable()
    .notRequired(),
});

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const Filter = (props: any) => {
  const [selectedFiltersCount, setSelectedFiltersCount] = useState<number>(0);
  const { handleSaveFilter, open, setOpen, search, filters } = props;
  const dispatch = useDispatch();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    setTimeout(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({
          top: scrollContainerRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    }, 100);
  };
  const { industries, departments, cities, jobRoles, hiringCompanies } = useSelector((state: any) => state.app);

  useEffect(() => {
    if (!cities?.length) {
      dispatch(getMasterData("CITY", SET_CITIES));
    }
    if (!industries?.length) {
      dispatch(getMasterData("INDUSTRY", SET_INDUSTRIES));
    }
    if (!departments?.length) {
      dispatch(getMasterData("DEPARTMENTS", SET_DEPARTMENTS));
    }
    if (!hiringCompanies?.length) {
      dispatch(getMasterData("HIRING_COMPANIES", SET_HIRING_COMPANIES));
    }
  }, []);

  const handleClickOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const { handleSubmit, control, reset, watch, setValue, getValues } = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver<FormValues>(schema),
    reValidateMode: "onChange",
  });

  const watchFunction = watch("function");

  // When preferred function is removed/changed, clear only job roles that are no longer in the new jobRoles list
  useEffect(() => {
    if (!jobRoles?.length) return;
    const currentJobRole = getValues("jobRole");
    if (!currentJobRole?.length) return;
    const roleValues = Array.isArray(jobRoles) ? jobRoles : [];
    const filtered = (Array.isArray(currentJobRole) ? currentJobRole : []).filter(
      (ro: { value: string }) => roleValues.includes(ro.value)
    );
    if (filtered.length < (currentJobRole?.length || 0)) {
      setValue("jobRole", filtered);
    }
  }, [jobRoles]);

  useEffect(() => {
    setValue("jobRole", getKeyValuePairs(filters.jobRole));
    if (filters?.function.length) {
      setValue("function", { label: filters.function?.[0], value: filters?.function?.[0] });
    } else {
      setValue("function", null);
    }
    setValue("type", filters.type || "");
    setValue("company", getKeyValuePairs(filters.company));
    setValue("industry", getKeyValuePairs(filters.industry));
    setValue("jobType", getKeyValuePairs(filters.jobType));
    setValue("workMode", getKeyValuePairs(filters.workMode));
    setValue("location", getKeyValuePairs(filters.location));
    setValue("minSal", filters.minSal);
    setValue("maxSal", filters.maxSal);
    setValue("minExp", filters.minExp || "");
    setValue("maxExp", filters.maxExp || "");

    const count = calculateSelectedFiltersCount(filters);
    setSelectedFiltersCount(count);
  }, [filters]);

  useEffect(() => {
    if (watchFunction?.value) {
      dispatch(getMasterData("JOB_ROLES", SET_JOB_ROLES, `parentId=${watchFunction?.value}`));
    }
  }, [watchFunction]);

  const onSubmit = (data: FormValues) => {
    console.log(data);
    const payload = {
      type: data?.type,
      company: data?.company?.map?.((el: any) => el?.value),
      function: data?.function?.value ? [data?.function.value] : [],
      jobRole: data?.jobRole?.map((el: any) => el?.value),
      industry: data?.industry?.map((el: any) => el?.value),
      jobType: data?.jobType?.map?.((el: any) => el?.value),
      workMode: data?.workMode?.map?.((el: any) => el?.value),
      // city: data?.location?.map?.((el: any) => el?.value),
      location: data?.location?.map?.((el: any) => el?.value),
      minSal: data?.minSal,
      maxSal: data?.maxSal,
      minExp: data?.minExp,
      maxExp: data?.maxExp,
    };

    if (!search) {
      const count = calculateSelectedFiltersCount(data);
      setSelectedFiltersCount(count);
    }

    handleSaveFilter(payload);
  };

  const calculateSelectedFiltersCount = (data: FormValues) => {
    let count = 0;
    let salaryCounted = false;

    Object.keys(data).forEach((key) => {
      if (key === "jobCategory" || key === "maxSal") return;

      if ((key === "minSal" || key === "maxSal") && !salaryCounted) {
        if (data.minSal || data.maxSal) {
          count++;
          salaryCounted = true;
        }
        return;
      }

      if (Array.isArray(data[key])) {
        count += data[key].length;
      } else if (typeof data[key] === "number" && !isNaN(data[key])) {
        count++;
      }
    });

    return count;
  };

  const resetFilters = () => {
    reset();
    onSubmit({
      type: "",
      company: [],
      function: null,
      jobRole: [],
      industry: [],
      jobType: [],
      workMode: [],
      location: [],
      minSal: null,
      maxSal: null,
      minExp: "",
      maxExp: "",
    });
  };

  return (
    <>
      <Button
        variant="contained"
        sx={{
          textTransform: "capitalize",
          width: "fit-content",
          cursor: "pointer",
          boxShadow: "none",
          backgroundColor: "#F4F4F4",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "70px",
          "&:hover": {
            backgroundColor: "#F4F4F4",
          },
        }}
        className="text-dark px-2"
        onClick={handleClickOpen}
      >
        {selectedFiltersCount > 0 ? (
          <Badge
            badgeContent={selectedFiltersCount}
            sx={{
              "& .MuiBadge-badge": {
                backgroundColor: "rgb(255, 137, 96)",
                color: "#fff",
                right: 8,
                top: 8,
                border: `2px solid white`,
                padding: "0 4px",
              },
            }}
          >
            <Box
              sx={{
                width: "50px",
                height: "50px",
                border: "1px solid #756DE9",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxSizing: "border-box",
              }}
            >
              <img src="/images/filter.png" width="25px" alt="" />
            </Box>
          </Badge>
        ) : (
          <Box
            sx={{
              width: "50px",
              height: "50px",
              border: "1px solid #756DE9",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxSizing: "border-box",
            }}
          >
            <img src="/images/filter.png" width="25px" alt="" />
          </Box>
        )}
      </Button>

      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        fullScreen
        PaperProps={{
          sx: {
            marginTop: 20,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
          },
        }}
      >
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle
            sx={{
              m: 0,
              p: 2,
              fontSize: "14px",
              letterSpacing: "1.67px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
            id="customized-dialog-title"
          >
            <Typography sx={{ fontWeight: 600 }}>Add Filter</Typography>
            <Box
              aria-label="close"
              onClick={handleClose}
              sx={{
                fontSize: "14px",
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                color: "#756DE9",
                fontWeight: "600",
              }}
            >
              CLOSE
              <IconButton
                size="small"
                sx={{
                  color: "#756DE9",
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pb: "0 !important", overflow: "visible", paddingBottom: "16px" }}>
            <Box
              ref={scrollContainerRef}
              display="flex"
              flexDirection="column"
              sx={{ overflow: "auto", maxHeight: "60vh" }}
            >
              <Grid container spacing={3} pl={3}>
                <Controller
                  name="type"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <Box sx={{ display: "flex", gap: 5, mt: 4 }}>
                      {jobTypes.map((jobType) => (
                        <Grid item xs={4} key={jobType.value}>
                          <FormControlLabel
                            control={
                              <Radio
                                value={jobType.value}
                                onChange={(e) => {
                                  const newType = e.target.value;
                                  if (newType !== value) {
                                    setValue("minSal", null);
                                    setValue("maxSal", null);
                                  }
                                  onChange(e.target.value);
                                }}
                                checked={value === jobType.value}
                              />
                            }
                            label={
                              <JobType>
                                <Typography>{jobType.label}</Typography>
                              </JobType>
                            }
                          />
                        </Grid>
                      ))}
                    </Box>
                  )}
                />
              </Grid>

              <Typography mt={2} sx={{ fontSize: "14px", fontWeight: 600 }}>
                Company
              </Typography>
              <MultiSelect noError name="company" control={control} options={getKeyValuePairs(hiringCompanies)} />

              <Typography mt={2} sx={{ fontSize: "14px", fontWeight: 600 }}>
                Preferred Function
              </Typography>
              <MultiSelect
                multiple={false}
                noError
                name="function"
                control={control}
                options={getKeyValuePairs(departments)}
              />

              <Typography mt={2} sx={{ fontSize: "14px", fontWeight: 600 }}>
                Preferred Job Role
              </Typography>
              <MultiSelect
                disabled={!watchFunction?.value}
                noError
                name="jobRole"
                control={control}
                options={getKeyValuePairs(jobRoles)}
              />
              <Typography mt={2} sx={{ fontSize: "14px", fontWeight: 600 }}>
                Preferred Industry
              </Typography>
              <MultiSelect noError name="industry" control={control} options={getKeyValuePairs(industries)} />
              <Typography mt={2} sx={{ fontSize: "14px", fontWeight: 600 }}>
                Preferred Job Type
              </Typography>
              <MultiSelect noError name="jobType" control={control} options={getKeyValuePairs(JOB_TYPES)} />
              <Typography mt={2} sx={{ fontSize: "14px", fontWeight: 600 }}>
                Preferred Work Mode
              </Typography>
              <MultiSelect noError name="workMode" control={control} options={getKeyValuePairs(WORK_MODES)} />
              <Typography mt={2} sx={{ fontSize: "14px", fontWeight: 600 }}>
                {watch("type") === "Internship" ? "Stipend" : "Salary"} Range Expected Per Month
              </Typography>
              <Grid container spacing={2} mt={-1}>
                <Grid item xs={6}>
                  <Controller
                    control={control}
                    name="minSal"
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <CssSelectTextField fullWidth error={!!error}>
                          <Select
                            sx={{
                              borderRadius: "99px",
                              boxShadow: "inset 0 0 11px 2px #dfd9d9",
                            }}
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            value={field?.value ?? ""}
                            error={!!error}
                            onChange={field.onChange}
                            displayEmpty={true}
                            renderValue={(value) => (value ? value.toLocaleString() : "Minimum")}
                          >
                            {Array.from({ length: 50 }).map((_, index) => {
                              const minSal = watch("type") === "Internship" ? (index + 1) * 1000 : (index + 1) * 100000;
                              return (
                                <MenuItem key={minSal} value={minSal}>
                                  {minSal.toLocaleString()}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </CssSelectTextField>
                        <FormHelperText sx={{ color: "#d32f2f", mt: 0, ml: 2 }}>
                          {error ? error?.message : " "}
                        </FormHelperText>
                      </>
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    control={control}
                    name="maxSal"
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <CssSelectTextField fullWidth error={!!error}>
                          <Select
                            sx={{
                              borderRadius: "99px",
                              boxShadow: "inset 0 0 11px 2px #dfd9d9",
                            }}
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            value={field?.value ?? ""}
                            error={!!error}
                            onChange={field.onChange}
                            displayEmpty={true}
                            renderValue={(value) => (value ? value.toLocaleString() : "Maximum")}
                          >
                            {Array.from({ length: 50 }).map((_, index) => {
                              const minSal = watch("type") === "Internship" ? (index + 1) * 1000 : (index + 1) * 100000;
                              return (
                                <MenuItem key={minSal} value={minSal}>
                                  {minSal.toLocaleString()}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </CssSelectTextField>
                        <FormHelperText sx={{ color: "#d32f2f", mt: 0, ml: 2 }}>
                          {error ? error?.message : " "}
                        </FormHelperText>
                      </>
                    )}
                  />
                </Grid>
              </Grid>
              <Typography mt={2} sx={{ fontSize: "14px", fontWeight: 600 }}>
                Preferred Location
              </Typography>
              <Box pb={2}>
                <MultiSelect
                  noError
                  name="location"
                  control={control}
                  options={getKeyValuePairs(cities)}
                  onMenuOpen={scrollToBottom}
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              position: "sticky",
              bottom: 0,
              backgroundColor: "white",
              paddingTop: "5px !important",
              paddingBottom: "16px",
              boxShadow: "0 -2px 10px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Button
              sx={{
                backgroundColor: "white",
                borderRadius: "99px",
                fontSize: "16px",
                textTransform: "capitalize",
                width: "50%",
                color: "#000000",
                "&:hover ": {
                  backgroundColor: "#f5f5f5", // Change hover background color if needed
                },
              }}
              variant="contained"
              size="small"
              style={{ padding: "15px 20px" }}
              className="w-100 mt-2 btn-pad"
              onClick={resetFilters}
            >
              Clear Filter
            </Button>

            <Button
              sx={{
                backgroundColor: "#756DE9",
                borderRadius: "99px",
                fontSize: "16px",
                textTransform: "capitalize",
                width: "50%",
                paddingY: "15px",
                paddingX: 0,
                "&:hover": {
                  backgroundColor: "#756DE9",
                },
              }}
              variant="contained"
              size="small"
              className="w-100 mt-2 btn-pad"
              type="submit"
            >
              Apply Filter
            </Button>
          </DialogActions>
        </Box>
      </BootstrapDialog>
    </>
  );
};

export default Filter;
